/* eslint-disable no-console */
import * as signalR from '@microsoft/signalr';
import { includes, values } from 'lodash';

import {
  SIGNALR_MESSAGES,
  SignalRMessage,
  SignalRMessageName,
} from '../config/signalRMessages';

import { acquireApiToken } from '@/auth/tokenProvider';
import { bufferingLog } from '@/utils/logging';

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private reconnectDelay = 5000; // 5 seconds
  private authHeader: string | null = null;
  private isReconnecting = false;
  private reconnectInterval: NodeJS.Timeout | null = null;
  private messageBuffer: SignalRMessage[] = [];
  private shouldBuffer = false;

  async startConnection(authHeader: string) {
    if (this.connection) {
      return;
    }

    this.authHeader = authHeader;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_API_BASE_URL}/notificationHub`, {
        accessTokenFactory: async () => {
          // The signalr client invokes this on every (re)negotiate. MSAL is
          // asked first so a reconnect never reuses an expired token; the
          // stored header stays as a pure fallback, never a downgrade.
          try {
            const token = await acquireApiToken();
            if (token) {
              return token;
            }
          } catch (error) {
            console.warn(
              'accessTokenFactory: MSAL token acquisition failed, using stored header.',
              error
            );
          }
          if (this.authHeader && this.authHeader.startsWith('Bearer ')) {
            return this.authHeader.substring(7);
          }
          console.warn(
            'accessTokenFactory: authHeader is missing or not a Bearer token.'
          );
          return '';
        },
        transport: signalR.HttpTransportType.WebSockets,
      })
      .configureLogging(signalR.LogLevel.Debug)
      .withStatefulReconnect()
      .build();

    this.connection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
      this.notifyListeners(SIGNALR_MESSAGES.Connection.Reconnecting, error);
    });

    this.connection.onreconnected((connectionId) => {
      this.isReconnecting = false;
      if (this.reconnectInterval) {
        clearInterval(this.reconnectInterval);
        this.reconnectInterval = null;
      }
      this.notifyListeners(
        SIGNALR_MESSAGES.Connection.Reconnected,
        connectionId
      );
    });

    this.connection.onclose((error) => {
      this.notifyListeners(SIGNALR_MESSAGES.Connection.Closed, error);
      this.handleConnectionClosed();
    });

    this.connection.on(
      SIGNALR_MESSAGES.WorkItem.RTP.Create,
      (createdWorkItem) => {
        this.notifyListeners(
          SIGNALR_MESSAGES.WorkItem.RTP.Create,
          createdWorkItem
        );
      }
    );

    this.connection.on(
      SIGNALR_MESSAGES.WorkItem.API.Create,
      (createdWorkItem) => {
        this.notifyListeners(
          SIGNALR_MESSAGES.WorkItem.API.Create,
          createdWorkItem
        );
      }
    );

    this.connection.on(
      SIGNALR_MESSAGES.WorkItem.API.Update,
      (updatedWorkItem) => {
        this.notifyListeners(
          SIGNALR_MESSAGES.WorkItem.API.Update,
          updatedWorkItem
        );
      }
    );

    this.connection.on(
      SIGNALR_MESSAGES.WorkItem.RTP.Update,
      (updatedWorkItem) => {
        this.notifyListeners(
          SIGNALR_MESSAGES.WorkItem.RTP.Update,
          updatedWorkItem
        );
      }
    );

    this.connection.on(
      SIGNALR_MESSAGES.WorkItem.API.Delete,
      (deletedWorkItem) => {
        this.notifyListeners(
          SIGNALR_MESSAGES.WorkItem.API.Delete,
          deletedWorkItem
        );
      }
    );

    this.connection.on(
      SIGNALR_MESSAGES.WorkItem.API.Restore,
      (restoredWorkItem) => {
        this.notifyListeners(
          SIGNALR_MESSAGES.WorkItem.API.Restore,
          restoredWorkItem
        );
      }
    );

    this.connection.on(SIGNALR_MESSAGES.WorkItem.API.Batch, (batchWorkItem) => {
      this.notifyListeners(SIGNALR_MESSAGES.WorkItem.API.Batch, batchWorkItem);
    });

    this.connection.on(
      SIGNALR_MESSAGES.HealthCheck.RTP.SnapshotCreate,
      (statusItems) => {
        this.notifyListeners(
          SIGNALR_MESSAGES.HealthCheck.RTP.SnapshotCreate,
          statusItems
        );
      }
    );

    [
      SIGNALR_MESSAGES.WorkItem.RTP.ScheduleImported,
      SIGNALR_MESSAGES.WorkItem.RTP.ScheduleImportedAll,
    ].forEach((method) => {
      this.connection?.on(method, (data) => this.notifyListeners(method, data));
    });

    try {
      await this.connection.start();
      this.isReconnecting = false;
      this.notifyListeners(SIGNALR_MESSAGES.Connection.Established, null);
      return true;
    } catch (err) {
      console.error(
        'SignalR HubConnection.start() failed during an attempt:',
        err
      );
      this.notifyListeners(SIGNALR_MESSAGES.Connection.Error, err);
      this.connection = null;
      return false;
    }
  }

  private stopReconnectInterval() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
      console.log('Custom reconnect interval cleared.');
    }
  }

  private stopReconnecting() {
    if (this.isReconnecting) {
      console.log('Stopping custom reconnection process.');
    }
    this.isReconnecting = false;
    this.stopReconnectInterval();
  }

  private async handleConnectionClosed() {
    console.log(
      'handleConnectionClosed called, current isReconnecting state:',
      this.isReconnecting
    );
    this.connection = null;

    if (this.isReconnecting) {
      console.log(
        'Custom reconnection process already active. This call will not start a new loop.'
      );
      return;
    }

    this.isReconnecting = true;
    this.notifyListeners(SIGNALR_MESSAGES.Connection.Reconnecting, {
      message: 'Custom reconnection process initiated by onclose.',
    });
    console.log('Starting custom reconnection process...');

    const attemptReconnect = async () => {
      if (!this.isReconnecting) {
        console.log(
          'Custom reconnection attempt aborted: isReconnecting is false.'
        );
        this.stopReconnectInterval();
        return;
      }

      if (!this.authHeader) {
        console.error(
          'Cannot attempt custom reconnect: authHeader is missing. Stopping reconnection attempts.'
        );
        this.notifyListeners(SIGNALR_MESSAGES.Reconnection.Failed, {
          error: 'AuthHeader missing for reconnect attempt.',
        });
        this.stopReconnecting();
        return;
      }

      try {
        console.log('Attempting custom reconnect via startConnection...');
        const success = await this.startConnection(this.authHeader);

        if (success) {
          console.log('Custom reconnection successful via startConnection.');
          this.notifyListeners(SIGNALR_MESSAGES.Connection.Reconnected, null);
          this.stopReconnecting();
        } else {
          console.warn(
            'Custom reconnection attempt via startConnection failed (returned false).'
          );
          this.notifyListeners(SIGNALR_MESSAGES.Reconnection.Failed, {
            error:
              'Custom startConnection attempt failed during reconnect loop.',
          });
        }
      } catch (error) {
        console.error(
          'Unexpected error during custom reconnection attempt:',
          error
        );
        this.notifyListeners(SIGNALR_MESSAGES.Reconnection.Failed, {
          error: 'Unexpected error in reconnect loop.',
          details: error,
        });
      }
    };

    this.reconnectInterval = setInterval(attemptReconnect, this.reconnectDelay);
    await attemptReconnect();
  }

  async stopConnection() {
    console.log('Stopping SignalR connection...');
    this.stopReconnecting();

    if (!this.connection) {
      console.log('No active SignalR connection instance to stop.');
      return;
    }

    try {
      await this.connection.stop();
      this.connection = null;
      this.notifyListeners(SIGNALR_MESSAGES.Connection.Stopped, null);
      console.log('SignalR connection stopped successfully');
    } catch (err) {
      console.error('Error stopping SignalR connection:', err);
      this.notifyListeners(SIGNALR_MESSAGES.Connection.StopError, err);
    }
  }

  async invokeMethod(methodName: string, ...args: any[]) {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      this.notifyListeners(SIGNALR_MESSAGES.Connection.NotEstablished, {
        methodName,
      });
      return null;
    }

    try {
      const result = await this.connection.invoke(methodName, ...args);
      this.notifyListeners(SIGNALR_MESSAGES.Method.Result, {
        methodName,
        result,
      });
      return result;
    } catch (err) {
      this.notifyListeners(SIGNALR_MESSAGES.Method.Error, {
        methodName,
        error: err,
      });
      return null;
    }
  }

  on(event: SignalRMessageName, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  startBuffering() {
    if (this.shouldBuffer) {
      return;
    }

    this.shouldBuffer = true;
    bufferingLog('Message buffering started');
  }

  stopBuffering() {
    if (!this.shouldBuffer) {
      return;
    }

    this.shouldBuffer = false;
    bufferingLog('Message buffering stopped');
  }

  clearBuffer() {
    const clearedCount = this.messageBuffer.length;
    this.messageBuffer = [];
    bufferingLog(`Message buffer cleared (${clearedCount} messages removed)`);
  }

  processBufferedMessages() {
    if (this.messageBuffer.length === 0) {
      bufferingLog('No buffered messages to process');
      return;
    }

    const messagesToProcess = [...this.messageBuffer];

    messagesToProcess.forEach((message, index) => {
      bufferingLog(
        `Processing buffered message ${index + 1}/${messagesToProcess.length}:`,
        {
          event: message.event,
          timestamp: message.timestamp.toISOString(),
        }
      );

      // Re-notify listeners with the buffered message
      this.listeners.get(message.event)?.forEach((callback) => {
        bufferingLog(`Calling listener for ${message.event}`);
        callback(message.data);
      });
    });

    bufferingLog('Finished processing all buffered messages');
  }

  stopBufferingAndProcess() {
    this.stopBuffering();
    this.processBufferedMessages();
  }

  private notifyListeners(event: string, data: any) {
    const workItemMessages = [
      ...values(SIGNALR_MESSAGES.WorkItem.API),
      ...values(SIGNALR_MESSAGES.WorkItem.RTP),
    ];

    const isWorkItemMessage = includes(workItemMessages, event);

    if (isWorkItemMessage && this.shouldBuffer) {
      const message: SignalRMessage = {
        timestamp: new Date(),
        event,
        data,
      };
      this.messageBuffer.push(message);

      bufferingLog('Message Buffered', {
        timestamp: message.timestamp.toISOString(),
        event: message.event,
        bufferedCount: this.messageBuffer.length,
      });
    }

    this.listeners.get(event)?.forEach((callback) => callback(data));
  }

  async reconnectWithNewAuthHeader(newAuthHeader: string): Promise<boolean> {
    console.log('Attempting to reconnect with new auth header...');
    this.authHeader = newAuthHeader;

    this.stopReconnecting();

    if (this.connection) {
      console.log(
        'Existing connection found. Stopping it before reconnecting with new auth header.'
      );
      const previousIsReconnectingState = this.isReconnecting;
      this.isReconnecting = true;

      try {
        await this.connection.stop();
        console.log('Previous connection stopped successfully.');
      } catch (stopError) {
        console.error(
          'Error stopping existing connection during auth header refresh:',
          stopError
        );
        this.notifyListeners('stopConnectionError', stopError);
      } finally {
        this.connection = null;
        this.isReconnecting = previousIsReconnectingState;
      }
    } else {
      console.log(
        'No existing connection. Proceeding to start new connection with new auth header.'
      );
    }

    console.log('Calling startConnection with new auth header.');
    const success = await this.startConnection(this.authHeader);

    if (success) {
      console.log(
        'Successfully reconnected with new auth header (startConnection successful).'
      );
      this.notifyListeners(SIGNALR_MESSAGES.Connection.Reconnected, null);
      return true;
    } else {
      console.error(
        'Failed to reconnect with new auth header (startConnection failed).'
      );
      this.notifyListeners(SIGNALR_MESSAGES.Reconnection.Failed, {
        error: 'Failed to establish connection with new auth header.',
      });
      return false;
    }
  }

  // Method for testing - simulate receiving a SignalR message
  sendTestMessage(event: string, data: any) {
    this.notifyListeners(event, data);
  }
}

const signalRService = new SignalRService();
export default signalRService;
